import React from 'react';
import {Observable} from 'rxjs';
import i18next from 'i18next';
import {List, Map} from 'immutable';

import {combineEpics} from 'web-app/util/redux-observable';
import TranslatedMarkdown from 'web-app/react/components/markdown/translated-markdown';
import {showDialogObservable, showDeleteDialogObservable, DialogChoice} from 'web-app/react/dialog';
import * as GlobalEventActions from 'web-app/react/global-event/actions';
import * as FileHelpers from 'web-app/react/entities/files/helpers';
import * as SentryService from 'web-app/services/sentry';
import InvoiceBehaviors from 'web-app/behaviors/invoice-behavior-ids';
import * as BillPayerActions from 'web-app/react/entities/bill-payers/actions';
import * as AccountItemActions from 'web-app/react/entities/account-items/actions';
import BehaviorsCollection from 'web-app/behaviors/behaviors-collection';
import {hasValue} from 'web-app/util/typescript';

import * as Helpers from './helpers';
import * as Api from './api';
import * as Actions from './actions';

const fetchObservationsObservable = feedItems => {
    if (feedItems.some(Helpers.feedItemIsObservation)) {
        const observationIds = feedItems
            .filter(Helpers.feedItemIsObservation)
            .map(item => item.embed.get('observationId'))
            .toArray();

        return Api.fetchObservations(observationIds).map(({observations, frameworks}) => {
            return feedItems.map(item => Helpers.getObservationFeedItem(item, observations, frameworks));
        });
    }
    return Observable.of(feedItems);
};

// Fetch bill payer information if the logged in user can pay for the displayed invoices.
const fetchBalanceActions = feedItems => {
    return feedItems
        .reduce((billPayerIds, item) => {
            if (!Helpers.feedItemIsInvoice(item)) {
                return billPayerIds;
            }

            const invoiceEmbed = item.embed.get('invoice');
            const invoiceBehaviors = new BehaviorsCollection(invoiceEmbed.behaviors);

            if (
                invoiceBehaviors.includes(InvoiceBehaviors.CanPayInvoice) &&
                invoiceEmbed.billPayerId &&
                invoiceEmbed.institutionId
            ) {
                return billPayerIds.set(invoiceEmbed.billPayerId, invoiceEmbed.institutionId);
            } else {
                return billPayerIds;
            }
        }, Map())
        .entrySeq()
        .toList()
        .flatMap(([billPayerId, institutionId]) => {
            return [
                Observable.of(AccountItemActions.fetchBillPayerBalance.action(institutionId, billPayerId)),
                Observable.of(BillPayerActions.fetch.action(institutionId, billPayerId)),
            ];
        })
        .toArray();
};

export const fetchObservable = (olderThan = '', institutionId, heightTarget) =>
    Api.fetch(olderThan, institutionId, heightTarget).mergeMap(feedItems => {
        return Observable.merge(
            fetchObservationsObservable(feedItems).map(feedItems => Actions.fetchSuccess.action({feedItems})),
            ...fetchBalanceActions(feedItems),
        );
    });

export const fetchBookmarkObservable = (olderThan = '') =>
    Api.fetchBookmarks(olderThan).mergeMap(feedItems => {
        return Observable.merge(
            fetchObservationsObservable(feedItems).map(feedItems => Actions.fetchSuccess.action({feedItems})),
            ...fetchBalanceActions(feedItems),
        );
    });

export const fetchSingleObservable = feedItemId =>
    Api.fetchSingle(feedItemId).mergeMap(({feedItem}) => {
        return Observable.merge(
            fetchObservationsObservable(List([feedItem])).map(feedItems =>
                Actions.fetchSingleSuccess.action(feedItems.first()),
            ),
            ...fetchBalanceActions(List([feedItem])),
        );
    });

export const deleteItemObservable = (originatorId, feedItemId, observationId) => {
    const deleteObservable = observationId ? Api.deleteObservationPost(observationId) : Api.deletePost(originatorId);
    return deleteObservable.map(() => Actions.deleteItemSuccess.action(originatorId, feedItemId, observationId));
};

export const updateItemObservable = (
    postId,
    recipients,
    from,
    body,
    richTextBody,
    files,
    observationData,
    disableFeedback,
    reNotify,
    activities,
) => {
    const {imageIds, videoIds, fileIds} = FileHelpers.getFileArrays(files);

    const itemIsObservation = hasValue(observationData);

    const updateObservable = itemIsObservation
        ? Api.updateObservationPost(
              postId,
              recipients,
              from,
              body,
              richTextBody,
              observationData.nextStep,
              imageIds,
              videoIds,
              fileIds,
              observationData.statements,
              observationData.areaRefinements,
              observationData.observedAt,
              observationData.variant,
          )
        : Api.updatePost(postId, body, richTextBody, disableFeedback, reNotify, imageIds, fileIds, activities);
    return updateObservable.map(data => Actions.updateItemSuccess.action(postId, data, itemIsObservation));
};

export const fetchCommentsAndLikesObservable = originatorId =>
    Api.showCommentsAndLikes(originatorId).map(response =>
        Actions.fetchCommentsAndLikesSuccess.action(originatorId, response.likes, response.comments),
    );

const fetch = action$ =>
    action$
        .ofType(Actions.fetch.type)
        .mergeMap(({payload}) =>
            fetchObservable(payload.olderThan, payload.institutionId).catch(e =>
                Observable.of(Actions.fetchFailed.action(e)),
            ),
        );

const fetchSingle = action$ =>
    action$
        .ofType(Actions.fetchSingle.type)
        .mergeMap(({payload}) =>
            fetchSingleObservable(payload.feedItemId).catch(e => Observable.of(Actions.fetchSingleFailed.action(e))),
        );

const updateItem = action$ =>
    action$
        .ofType(Actions.updateItem.type)
        .mergeMap(({payload}) =>
            updateItemObservable(
                payload.postId,
                payload.recipients,
                payload.from,
                payload.body,
                payload.richTextBody,
                payload.files,
                payload.observationData,
                payload.disableFeedback,
                payload.activities,
            ).catch(e => Observable.of(Actions.updateItemFailed.action(e))),
        );

const like = action$ =>
    action$.ofType(Actions.like.type).mergeMap(({payload}) =>
        Api.like(payload.originatorId)
            .mergeMap(() =>
                Observable.concat(
                    Observable.of(Actions.likeSuccess.action(payload.originatorId)),
                    fetchCommentsAndLikesObservable(payload.originatorId),
                ),
            )
            .catch(e => Observable.of(Actions.likeFailed.action(e))),
    );

const unlike = action$ =>
    action$.ofType(Actions.unlike.type).mergeMap(({payload}) =>
        Api.unlike(payload.originatorId)
            .mergeMap(() =>
                Observable.concat(
                    Observable.of(Actions.unlikeSuccess.action(payload.originatorId)),
                    fetchCommentsAndLikesObservable(payload.originatorId),
                ),
            )
            .catch(e => Observable.of(Actions.unlikeFailed.action(e))),
    );

const comment = action$ =>
    action$.ofType(Actions.comment.type).mergeMap(({payload}) => {
        if (!payload.body) {
            // The backend is getting events where body is not set.
            // We can't reproduce so try to log this to see what happens in sentry.
            SentryService.captureMessage('Comment body is empty');

            return Observable.concat(
                Observable.of(GlobalEventActions.updateSuccess.action(i18next.t('feed.commentMustNotBeEmpty'))),
                Observable.of(Actions.commentAborted.action(payload.originatorId)),
            );
        }

        return Observable.concat(
            Api.comment(payload.originatorId, payload.body, payload.imageIds)
                .mergeMap(response =>
                    Observable.concat(
                        Observable.of(Actions.commentSuccess.action(payload.originatorId, response.comment)),
                        fetchCommentsAndLikesObservable(payload.originatorId),
                    ),
                )
                .catch(e =>
                    Observable.concat(
                        Observable.of(Actions.commentFailed.action({error: e, originatorId: payload.originatorId})),
                    ),
                ),
        );
    });

const deleteComment = action$ =>
    action$.ofType(Actions.deleteComment.type).mergeMap(({payload}) =>
        showDeleteDialogObservable({title: '', description: i18next.t('feed.confirmDeleteComment')}).mergeMap(
            selection => {
                if (selection === DialogChoice.OK) {
                    return Api.deleteComment(payload.commentId)
                        .map(() => Actions.deleteCommentSuccess.action(payload.originatorId, payload.commentId))
                        .catch(e => Observable.of(Actions.deleteCommentFailed.action(e)));
                }

                return Observable.of(Actions.deleteCommentAborted.action(payload.originatorId, payload.commentId));
            },
        ),
    );

const likeComment = action$ =>
    action$.ofType(Actions.likeComment.type).mergeMap(({payload}) =>
        Api.likeComment(payload.commentId, payload.like)
            .mergeMap(() =>
                Observable.concat(
                    Observable.of(
                        Actions.likeCommentSuccess.action(payload.originatorId, payload.commentId, payload.like),
                    ),
                    fetchCommentsAndLikesObservable(payload.originatorId),
                ),
            )
            .catch(e => Observable.of(Actions.likeCommentFailed.action(e))),
    );

const deleteItem = action$ =>
    action$.ofType(Actions.deleteItem.type).mergeMap(({payload}) =>
        showDeleteDialogObservable({
            title: i18next.t('feed.deletePostHeader'),
            description: React.createElement(TranslatedMarkdown, {translationKey: 'feed.deletePost'}),
            okText: i18next.t('feed.deletePostConfirm'),
        }).mergeMap(selection => {
            if (selection === DialogChoice.OK) {
                return deleteItemObservable(payload.originatorId, payload.feedItemId, payload.observationId)
                    .flatMap(successAction => [
                        successAction,
                        GlobalEventActions.updateSuccess.action(i18next.t('feed.deletePostSuccess')),
                    ])
                    .catch(e => Observable.of(Actions.deleteItemFailed.action(e)));
            }

            return Observable.of(Actions.deleteItemAborted.action(payload.originatorId));
        }),
    );

const bookmarkItem = action$ =>
    action$.ofType(Actions.bookmarkItem.type).mergeMap(({payload}) =>
        Api.bookmarkPost(payload.feedItemId)
            .flatMap(() => [
                Actions.bookmarkItemSuccess.action(payload.feedItemId),
                GlobalEventActions.updateSuccess.action(i18next.t('feed.bookmarkPostSuccess')),
            ])
            .catch(e => Observable.of(Actions.bookmarkItemFailed.action(payload.feedItemId, e))),
    );

const unBookmarkItem = action$ =>
    action$.ofType(Actions.unBookmarkItem.type).mergeMap(({payload}) =>
        showDialogObservable({title: '', description: i18next.t('feed.unBookmarkConfirmDescription')}).mergeMap(
            selection => {
                if (selection === DialogChoice.OK) {
                    return Api.unBookmarkPost(payload.feedItemId)
                        .flatMap(() => [
                            Actions.unBookmarkItemSuccess.action(payload.feedItemId),
                            GlobalEventActions.updateSuccess.action(i18next.t('feed.unBookmarkPostSuccess')),
                        ])
                        .catch(e => Observable.of(Actions.unBookmarkItemFailed.action(payload.feedItemId, e)));
                } else {
                    return Observable.of(Actions.unBookmarkItemAborted.action(payload.feedItemId));
                }
            },
        ),
    );

export default combineEpics(
    fetch,
    fetchSingle,
    like,
    unlike,
    comment,
    deleteComment,
    deleteItem,
    updateItem,
    likeComment,
    bookmarkItem,
    unBookmarkItem,
);
